import { upperFirst } from 'lodash-es'
import pluralize from 'pluralize'
import { type Ref, useCallback, useMemo } from 'react'

import type {
  ComboboxHandle,
  LabeledOption,
} from '@fv/client-components/combobox'
import type { ClientPackagingType, ProductCatalogEntry } from '@fv/client-types'

import { ShipperAutocomplete } from '@/components/inputs/ShipperAutocomplete'
import { ShipperTypeahead } from '@/components/inputs/ShipperTypeahead'
import { useTypeNames } from '@/hooks/settings'

import { getDimensions } from './commodityUtils'
import { useProducts } from './useHandlingUnits'
import type { ProductCatalogFilter, SetProduct } from './useProductCatalog'

type ProductOption = LabeledOption<ProductCatalogEntry>

type Props = {
  readOnly?: boolean
  filter: ProductCatalogFilter
  name: string
  setProduct: SetProduct
  value: string
  onInputChange: (value: string) => void
  searchRef?: Ref<ComboboxHandle>
  required?: boolean
  doublesAsDescription?: boolean
}
export const ProductCatalogTypeahead = ({
  readOnly,
  filter,
  onInputChange,
  searchRef,
  setProduct,
  value,
  required,
  doublesAsDescription,
}: Props) => {
  const { packageName } = useTypeNames()
  const productsQuery = useProducts(true, filter, value)

  const productOptions: ProductOption[] = useMemo(
    () =>
      productsQuery.data.map(v => ({
        label: composeProductLabel(v, packageName),
        value: v,
      })),
    [productsQuery.data, packageName],
  )
  const selectedOption: ProductOption | null = useMemo(() => {
    if (!value) return null
    const match = productOptions.find(o => o.label === value)
    if (match) return match
    if (doublesAsDescription) {
      return {
        label: value,
        value: { description: value } as ProductCatalogEntry,
      }
    }
    return null
  }, [productOptions, value, doublesAsDescription])

  const handleSelect = useCallback(
    (option: ProductOption | null) => {
      if (!option) return
      setProduct(option.value)
    },
    [setProduct],
  )

  if (doublesAsDescription) {
    return (
      <ShipperAutocomplete
        classNames={productCatalogClassNames}
        ref={searchRef}
        isLoading={productsQuery.isFetching}
        name="productSearch"
        onSelect={handleSelect}
        onValueChange={onInputChange}
        getOptionLabel={getProductOptionLabel}
        getOptionValue={getProductOptionValue}
        itemToStringValue={getProductDescription}
        filter={null}
        options={productOptions}
        value={value}
        required={required}
        readOnly={readOnly}
      />
    )
  }

  return (
    <ShipperTypeahead
      classNames={productCatalogClassNames}
      ref={searchRef}
      isClearable={false}
      isLoading={productsQuery.isFetching}
      name="productSearch"
      onSelect={handleSelect}
      getOptionLabel={getProductOptionLabel}
      getOptionValue={getProductOptionValue}
      onInputValueChange={onInputChange}
      options={productOptions}
      value={selectedOption}
      required={required}
      readOnly={readOnly}
    />
  )
}

const productCatalogClassNames = { container: 'w-full' }

function composeProductLabel(
  product: ProductCatalogEntry,
  getPackageName: (key: ClientPackagingType) => string,
) {
  const {
    description,
    freightClass,
    hazardous,
    nmfc,
    pieces,
    saidToContain,
    saidToContainPackagingType,
    stackable,
    weight,
  } = product

  const dimensions = getDimensions(product)
  let label = description.trim()

  if (product.package) {
    const pkgName = getPackageName(product.package)
    label += ` - ${pluralize(pkgName, 2)}`
  }

  if (dimensions) label += ` - ${dimensions}`
  if (pieces) label += ` - ${pieces} pieces`
  if (weight) label += ` - ${weight} lbs`
  if (freightClass) label += ` - Class ${freightClass}`
  if (nmfc) label += ` - NMFC# ${nmfc}`

  if (saidToContain || saidToContainPackagingType) {
    const quantity = Number(saidToContain || 2)
    const packageType = pluralize(saidToContainPackagingType, quantity)
    const contains = `${saidToContain} ${upperFirst(packageType)}`.trim()
    label += ` - Contains ${contains}`
  }

  if (stackable === 'Yes') label += ' - Stackable'
  if (hazardous === 'Yes') label += ' - Hazardous'

  return label
}

const getProductOptionLabel = (option: ProductOption) => option.label
const getProductOptionValue = (option: ProductOption) =>
  option.value._id ?? option.label
const getProductDescription = (option: ProductOption) =>
  option.value.description
